import React, { Component } from 'react';
import { compose } from 'utils/compose';
import { inject, observer } from 'mobx-react';
import { useParams, useLocation } from 'react-router-dom';
import { Avatar, Box, Chip, CircularProgress, Container, Grid, Typography } from '@mui/material';
import { useStyles } from '../main/styles';
import withMediaQuery from '../../constants/mediaQuery';

// HOC to inject styles into class component
// no-dd-sa:javascript-code-style/parameter-name
const withUseStyles = (WrappedComponent) => {
  return function WithStylesWrapper(props) {
    const classes = useStyles();
    return <WrappedComponent {...props} classes={classes} />;
  };
};
import _ from 'lodash';
import { renderDeliveryTimeAndLabel, translateEvents } from '../../utils/events';
import { eventDescriptionStyle } from '../../utils/eventDescriptionStyle';
import * as SHIPMENT_STATUS from '../../constants/shipmentStatus';
import { Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material';
import moment from 'moment';
import { isoToLocalHuman } from '../../utils/calendar';
import { resolveDeliveryWindow } from '../../utils/deliveryDate';
import TrackingMap from '../../components/TrackingMap';
import EventTimelineIcon from '../../components/TrackingCode/EventTimelineIcon';

class ListContainer extends Component {
  constructor(props) {
    super(props);
  }

  componentDidMount() {
    const { store, match } = this.props;
    const { deliveryStore } = store;

    if (match?.params && match.params.tracking_codes && !_.includes(['404', 'error'], match.params.tracking_codes)) {
      const { tracking_codes } = match.params;
      const trackingList = tracking_codes
        .split(',')
        .map((code) => code.trim())
        .filter((code) => !!code);
      deliveryStore.listDeliveries(trackingList, (res) => {
        // 404 not found
        if (res.status === 404 || !res.data || !res.data.length) {
          if (_.includes(match.url, '/noredirect/')) {
            window.location.href = `/404`;
          } else {
            window.location.href = `/`;
          }
        }
      });
    }
  }

  render() {
    const { store, classes, mediaQuery, hideCompanyBranding } = this.props;
    const { deliveryStore } = store;
    const { deliveries, loadingDelivery } = deliveryStore;
    const { pageTheme } = deliveryStore;
    if (loadingDelivery) {
      return (
        <Container maxWidth="lg" sx={classes.container}>
          <Grid style={{ height: 300 }} container alignItems="center" justifyContent="center">
            <CircularProgress color="primary" />
          </Grid>
        </Container>
      );
    }

    return (
      <Box>
        {deliveries.map((delivery) => {
          if (!delivery) return null;

          const { client, driver, settings, shipment, outbound_events, pickup, dropoff, no_service_dates } = delivery;
          const translatedEvents = translateEvents(outbound_events || [], shipment);
          const milestones = (outbound_events || []).map((e) => e.milestone).filter((m) => m); // translatedEvents.filter(e => VERBIAGE[e.convertedSignal]).map(e => VERBIAGE[e.convertedSignal].milestone);
          const milestone = milestones[0] || SHIPMENT_STATUS.PROCESSING;
          const progress = SHIPMENT_STATUS.progressByStatus(pageTheme);
          const deliveryTimeAndLabel = renderDeliveryTimeAndLabel(milestone, translatedEvents, shipment, settings, no_service_dates);
          let etaInTime = '';
          const inProgress = pickup && pickup.status === 'SUCCEEDED';
          if (dropoff && dropoff.status === 'SUCCEEDED') {
            etaInTime = 'Delivered';
          } else if (dropoff && (dropoff.status === 'FAILED' || dropoff.status === 'DISCARDED')) {
            etaInTime = '-';
          } else if (SHIPMENT_STATUS.FAILED === milestone) {
            etaInTime = '-';
          } else if (inProgress && dropoff?.estimated_arrival_ts) {
            const eta = moment(dropoff.estimated_arrival_ts).diff(moment.now());
            if (eta < 0) {
              etaInTime = '-';
            } else {
              const etaInSeconds = Math.ceil(eta / 1000);
              const etaInMinutes = Math.ceil(etaInSeconds / 60);
              const roundUp = Math.max(0, Math.floor((etaInMinutes + 4) / 5) * 5);
              const minutes = roundUp % 60;
              const hours = Math.floor(roundUp / 60);
              etaInTime = 'about ' + `${hours > 0 ? `${hours}h ` : ''}` + minutes + 'm';
            }
          } else {
            const { dropoff_latest_ts: dropoffLatestTs } = resolveDeliveryWindow(shipment, outbound_events, no_service_dates);

            etaInTime = 'by ' + isoToLocalHuman(dropoffLatestTs, shipment.timezone || 'America/Los_Angeles', dropoffLatestTs, 'hA MM/DD/YYYY');
            if (settings.hide_delivery_datetime === 'true' && (SHIPMENT_STATUS.PROCESSING === milestone || SHIPMENT_STATUS.PROCESSED === milestone)) {
              etaInTime = 'N/A';
            }
          }

          return (
            <Container key={shipment.id} maxWidth={false} disableGutters sx={classes.container}>
              <Container sx={classes.bodyContainer}>
                <Box mb={mediaQuery.isMobile ? 2 : 3} sx={classes.boxWrapper}>
                  <Box
                    p={mediaQuery.isMobile ? 2 : 4.5}
                    pb={mediaQuery.isMobile ? 3 : undefined}
                    sx={classes.statusContainer}
                    borderRadius="10px 10px 0 0"
                  >
                    <Box mb={2.5} sx={classes.statusTitle}>
                      <Box>
                        <Box component="span" sx={classes.statusText}>
                          Status:{' '}
                        </Box>
                        <Box component="span" sx={classes.statusTextBig}>
                          {milestone}
                        </Box>
                      </Box>
                      <Box>{shipment.attempt_count > 1 && <Chip size="small" sx={classes.reattemptButton} label={'2nd attempt'} />}</Box>
                    </Box>
                    <Box sx={classes.statusProgress} borderRadius="11px">
                      <Box sx={{ ...classes.statusIndicator, ...classes.statusInProgress, ...progress[milestone] }}>
                        {SHIPMENT_STATUS.FAILED_MILESTONE.indexOf(milestone) > -1 ? (
                          <CloseIcon sx={classes.whiteIcon} />
                        ) : (
                          <CheckIcon sx={classes.whiteIcon} />
                        )}
                      </Box>
                    </Box>
                  </Box>
                  <Grid container sx={classes.shipmentInfoContainer}>
                    <Grid size={{ xs: 12, sm: 6 }} sx={classes.shipmentInfoLeft}>
                      <Grid container wrap="nowrap" spacing={2} style={{ marginBottom: 15 }}>
                        {!hideCompanyBranding && (
                          <Grid>
                            <Avatar alt="Client Logo" src={client.logo_url} variant="square" sx={classes.clientLogo} />
                          </Grid>
                        )}
                        <Grid>
                          {!hideCompanyBranding && (
                            <Typography sx={classes.clientName} variant="h4">
                              {client.company}
                            </Typography>
                          )}
                          <Box style={{ fontSize: mediaQuery.isMobile ? 14 : 'inherit' }}>
                            <Box component="span" sx={classes.grayText}>
                              Tracking code:{' '}
                            </Box>
                            <Box component="span" sx={classes.boldText}>
                              {shipment.tracking_code}
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }} sx={classes.shipmentInfoRight}>
                      <Box pl={mediaQuery.isMobile ? 8.25 : 0}>
                        <Box style={{ marginBottom: 15 }}>
                          <Box sx={classes.grayTextWithMarginBtm}>{deliveryTimeAndLabel.label}</Box>
                          <Box sx={classes.boldText}>{deliveryTimeAndLabel.time}</Box>
                        </Box>
                        <Box>
                          <Box sx={classes.grayTextWithMarginBtm}>Deliver to:</Box>
                          <Box
                            sx={classes.boldText}
                          >{`${shipment.dropoff_address.city}, ${shipment.dropoff_address.state.toUpperCase()}, ${shipment.dropoff_address.zipcode}`}</Box>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
                <Box mb={4}>
                  <Grid container spacing={mediaQuery.isMobile ? 0 : 2} sx={classes.eventsAndMap}>
                    <Grid size={{ xs: 12, sm: 6 }} sx={classes.bodyItemWrapper}>
                      <Container sx={{ ...classes.bodyBottomItem, ...classes.boxWrapper }}>
                        <Grid container justifyContent="space-between">
                          <Grid size={7}>
                            <Box sx={classes.grayTextWithMarginBtm}>ETA</Box>
                            <Box sx={classes.boldText}>{etaInTime}</Box>
                          </Grid>
                          {settings.hide_delivery_vehicle_info !== 'true' && (
                            <Grid size={5} style={{ textAlign: 'right' }}>
                              <Box sx={classes.grayTextWithMarginBtm}>Delivery Vehicle</Box>
                              <Box sx={classes.boldText}>
                                {!!driver ? `${_.capitalize(driver.vehicle_make)} ${_.capitalize(driver.vehicle_model)}` : 'N/A'}
                              </Box>
                            </Grid>
                          )}
                        </Grid>
                        {settings.tracking_show_map === 'true' && (
                          <Box height={mediaQuery.isMobile ? 250 : 500} mt={2.5}>
                            <TrackingMap shipment={shipment} />
                          </Box>
                        )}
                        {settings.tracking_show_map !== 'true' && mediaQuery.isMobile && (
                          <Box style={{ marginTop: 35, height: 1, width: '100%', backgroundColor: '#ccc' }} />
                        )}
                      </Container>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }} sx={classes.bodyItemWrapper}>
                      <Container sx={{ ...classes.bodyBottomItem, ...classes.boxWrapper }}>
                        {translatedEvents.map((event, idx) => {
                          if (!event.description) return null;
                          const eventDate = moment(event.ts)
                            .tz(shipment.timezone || 'America/Los_Angeles')
                            .format('MMM DD');
                          const eventTime = moment(event.ts)
                            .tz(shipment.timezone || 'America/Los_Angeles')
                            .format('hh:mm A');

                          return (
                            <Grid key={`event-${idx}`} container sx={classes.deliveryStep} alignItems="center">
                              <Grid size={3} sx={{ ...classes.deliveryStepCell, ...classes.deliveryStepCellDateTime }}>
                                <Box sx={classes.deliveryStepDate}>{eventDate}</Box>
                                <Box sx={classes.deliveryStepTime}>{eventTime}</Box>
                              </Grid>
                              <Grid
                                size={1}
                                sx={{
                                  ...classes.deliveryStepCell,
                                  ...classes.deliveryStepCellIcon,
                                  ...(idx + 1 < translatedEvents.length && classes.deliveryStepConnector),
                                }}
                              >
                                <EventTimelineIcon event={event} />
                              </Grid>
                              <Grid size={9} sx={classes.deliveryStepCell}>
                                <Box sx={eventDescriptionStyle(event)}>{event.description}</Box>
                              </Grid>
                            </Grid>
                          );
                        })}
                      </Container>
                    </Grid>
                  </Grid>
                </Box>
              </Container>
            </Container>
          );
        })}
      </Box>
    );
  }
}

const ListContainerCompose = compose(inject('store'), observer)(ListContainer);

// Wrapper component to provide router hooks to class component
function ListContainerWrapper(props) {
  const params = useParams();
  const location = useLocation();
  return <ListContainerCompose {...props} match={{ params, url: location.pathname }} />;
}

export default withUseStyles(withMediaQuery()(ListContainerWrapper));
